// src/components/manager/ReporteEjecutivoAnaliticaPanel.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, collectionGroup, getDocs, onSnapshot } from 'firebase/firestore';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import {
    FaFilePdf,
    FaSyncAlt,
    FaCopy,
    FaWhatsapp,
    FaStar,
    FaBuilding,
    FaChartPie,
    FaChartBar,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './ReporteEjecutivoAnaliticaPanel.css';

const EXCLUDED_FRACC_NAMES = new Set(['demo wong iii', 'plaza misiones']);

const PALETTE = ['#4f46e5', '#10b981', '#0284c7', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const WEB_PORTAL_URL = 'domushabita.com.mx';

const EJEMPLOS_RESENAS_PLAYSTORE = [
    {
        titulo: 'Opción 1 · Comunicación y Orden Condominal',
        texto: '⭐⭐⭐⭐⭐ ¡Excelente aplicación para nuestro fraccionamiento! Ahora todos los comunicados, avisos, encuestas y votaciones están ordenados en un solo lugar sin perderse entre cientos de mensajes de WhatsApp. Muy intuitiva y rápida.',
    },
    {
        titulo: 'Opción 2 · Seguridad, Accesos QR y Paquetería',
        texto: '⭐⭐⭐⭐⭐ Súper práctica para el día a día. Generar códigos QR para mis visitas en segundos y recibir notificaciones inmediatas cuando llega un paquete a caseta nos brinda muchísima tranquilidad y control en el residencial.',
    },
    {
        titulo: 'Opción 3 · Transparencia Financiera y Autogestión',
        texto: '⭐⭐⭐⭐⭐ La mejor herramienta para residentes y administración. Puedo consultar mis estados de cuenta, recibos digitales, avisos oficiales y participar en las votaciones del fraccionamiento desde mi celular en cualquier momento.',
    },
];

const classifyDevice = (ua = '') => {
    if (!ua || typeof ua !== 'string') return 'navegador_web';
    const lower = ua.toLowerCase();
    if (
        lower.includes('iphone') ||
        lower.includes('ipad') ||
        lower.includes('ipod') ||
        (lower.includes('macintosh') && lower.includes('mobile'))
    ) {
        return 'ios_safari';
    }
    if (
        lower.includes('android') &&
        (lower.includes('; wv)') ||
            lower.includes(' wv)') ||
            lower.includes('version/4.0') ||
            lower.includes('capacitor') ||
            lower.includes('okhttp'))
    ) {
        return 'android_app';
    }
    return 'navegador_web';
};

const classifyRole = (rol = '') => {
    const r = String(rol || '').toLowerCase().trim();
    if (
        [
            'superadmin',
            'platformadmin',
            'plataforma',
            'admin',
            'administrador',
            'administrator',
            'manager',
            'admin_fracc',
            'gerente',
            'administracion',
        ].includes(r)
    ) {
        return 'administrador';
    }
    if (
        [
            'caseta',
            'guardia',
            'guard',
            'security',
            'vigilancia',
            'seguridad',
            'control-acceso',
            'control_acceso',
            'portero',
        ].includes(r)
    ) {
        return 'caseta';
    }
    return 'residente';
};

const toDateSafe = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts._seconds) return new Date(ts._seconds * 1000);
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
};

const getMexicoHour = (date) => {
    try {
        const fmt = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Mexico_City',
            hour: 'numeric',
            hour12: false,
        });
        const h = parseInt(fmt.format(date), 10);
        return h === 24 ? 0 : h;
    } catch {
        return date.getHours();
    }
};

const getMexicoYearMonth = (date) => {
    try {
        const fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Mexico_City',
            year: 'numeric',
            month: '2-digit',
        });
        return fmt.format(date);
    } catch {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    }
};

const MESES_OPCIONES = [
    { value: '2026-10', label: 'Octubre 2026 (Actual)' },
    { value: '2026-09', label: 'Septiembre 2026' },
    { value: '2026-08', label: 'Agosto 2026' },
    { value: '2026-07', label: 'Julio 2026' },
    { value: '2026-06', label: 'Junio 2026' },
    { value: '2026-05', label: 'Mayo 2026' },
    { value: '2026-04', label: 'Abril 2026' },
    { value: '2026-03', label: 'Marzo 2026' },
    { value: '2026-02', label: 'Febrero 2026' },
    { value: '2026-01', label: 'Enero 2026' },
];

const TRIMESTRES_OPCIONES = [
    { value: 'T4', label: '4to Trimestre 2026 (Oct - Dic)', months: ['2026-10', '2026-11', '2026-12'] },
    { value: 'T3', label: '3er Trimestre 2026 (Jul - Sep)', months: ['2026-07', '2026-08', '2026-09'] },
    { value: 'T2', label: '2do Trimestre 2026 (Abr - Jun)', months: ['2026-04', '2026-05', '2026-06'] },
    { value: 'T1', label: '1er Trimestre 2026 (Ene - Mar)', months: ['2026-01', '2026-02', '2026-03'] },
];

const SEMESTRES_OPCIONES = [
    {
        value: 'S2',
        label: '2do Semestre 2026 (Jul - Dic)',
        months: ['2026-07', '2026-08', '2026-09', '2026-10', '2026-11', '2026-12'],
    },
    {
        value: 'S1',
        label: '1er Semestre 2026 (Ene - Jun)',
        months: ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06'],
    },
];

const ANIOS_OPCIONES = [
    { value: '2026', label: 'Año 2026 Completo (Ene - Dic)' },
    { value: 'ALL', label: 'Histórico Completo Acumulado' },
];

const PREFERRED_ORDER = [
    'Cipreses II',
    'Claustros de Santa Fe 4',
    'Fraccionamiento Fiorelo QRO.',
    'Cipreses IV',
    'Agave Azul V',
    'Demo Opsa',
];

const ReporteEjecutivoAnaliticaPanel = () => {
    const [loading, setLoading] = useState(true);
    const [lastSyncText, setLastSyncText] = useState('');

    // Filtros de Temporalidad (Mes, Trimestre, Semestre, Año)
    const [periodMode, setPeriodMode] = useState('anio'); // 'mes' | 'trimestre' | 'semestre' | 'anio'
    const [selectedMonth, setSelectedMonth] = useState('2026-10');
    const [selectedQuarter, setSelectedQuarter] = useState('T4');
    const [selectedSemester, setSelectedSemester] = useState('S2');
    const [selectedYear, setSelectedYear] = useState('2026');

    // Fraccionamiento seleccionado ('GLOBAL' o fraccId)
    const [selectedFraccId, setSelectedFraccId] = useState('GLOBAL');

    // Tipos de gráfica por tarjeta ('bar' o 'pie')
    const [chartModes, setChartModes] = useState({
        evolucion: 'bar',
        punto8: 'bar',
        punto1: 'bar',
        punto2: 'bar',
        punto3: 'bar',
        punto4: 'pie',
        punto5: 'pie',
        punto6: 'bar',
        horarios: 'bar',
    });

    // Estado crudo sincronizado desde Firestore
    const [rawStore, setRawStore] = useState({
        fraccs: [],
        excludedIds: new Set(),
        users: [],
        activeSessions: [],
        subcollectionsByFracc: {},
    });

    const toggleChartMode = (key, mode) => {
        setChartModes((prev) => ({ ...prev, [key]: mode }));
    };

    // Carga reactiva y sincronización en tiempo real
    const fetchSubcollectionsForFraccs = useCallback(async (fraccsList) => {
        const map = {};
        await Promise.all(
            fraccsList.map(async (f) => {
                try {
                    const [casasSnap, paqSnap, accSnap, invSnap, notifSnap] = await Promise.all([
                        getDocs(collection(db, 'fraccionamientos', f.id, 'casas')),
                        getDocs(collection(db, 'fraccionamientos', f.id, 'paqueteria')),
                        getDocs(collection(db, 'fraccionamientos', f.id, 'accesosVisitantes')),
                        getDocs(collection(db, 'fraccionamientos', f.id, 'invitacionesAcceso')),
                        getDocs(collection(db, 'fraccionamientos', f.id, 'notificaciones')),
                    ]);
                    map[f.id] = {
                        casas: casasSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                        paqueteria: paqSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                        accesos: accSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                        invitaciones: invSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                        notificaciones: notifSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                    };
                } catch (err) {
                    console.warn(`[ReporteEjecutivoAnalitica] Error leyendo subcolecciones de ${f.nombre}:`, err);
                    map[f.id] = { casas: [], paqueteria: [], accesos: [], invitaciones: [], notificaciones: [] };
                }
            })
        );
        setRawStore((prev) => ({ ...prev, subcollectionsByFracc: map }));
        setLastSyncText(
            new Date().toLocaleTimeString('es-MX', {
                timeZone: 'America/Mexico_City',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            })
        );
        setLoading(false);
    }, []);

    useEffect(() => {
        setLoading(true);
        // 1. Listener en tiempo real para Fraccionamientos
        const unsubFraccs = onSnapshot(collection(db, 'fraccionamientos'), (snap) => {
            const excludedIds = new Set();
            const validFraccs = [];
            snap.docs.forEach((d) => {
                const data = d.data() || {};
                const cleanName = (data.nombre || '').trim().toLowerCase();
                if (EXCLUDED_FRACC_NAMES.has(cleanName)) {
                    excludedIds.add(d.id);
                } else {
                    validFraccs.push({ id: d.id, ...data });
                }
            });

            validFraccs.sort((a, b) => {
                const iA = PREFERRED_ORDER.indexOf(a.nombre);
                const iB = PREFERRED_ORDER.indexOf(b.nombre);
                if (iA !== -1 && iB !== -1) return iA - iB;
                if (iA !== -1) return -1;
                if (iB !== -1) return 1;
                return (a.nombre || '').localeCompare(b.nombre || '');
            });

            setRawStore((prev) => ({ ...prev, fraccs: validFraccs, excludedIds }));
            fetchSubcollectionsForFraccs(validFraccs);
        });

        // 2. Listener en tiempo real para Usuarios
        const unsubUsers = onSnapshot(collection(db, 'usuarios'), (snap) => {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setRawStore((prev) => ({ ...prev, users: list }));
            setLastSyncText(
                new Date().toLocaleTimeString('es-MX', {
                    timeZone: 'America/Mexico_City',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                })
            );
        });

        // 3. Listener en tiempo real para Sesiones Activas
        let unsubSessions = () => {};
        try {
            unsubSessions = onSnapshot(
                collectionGroup(db, 'sesiones_activas'),
                (snap) => {
                    const list = snap.docs.map((d) => ({
                        id: d.id,
                        userId: d.ref.parent?.parent?.id || d.data()?.userId,
                        ...d.data(),
                    }));
                    setRawStore((prev) => ({ ...prev, activeSessions: list }));
                },
                () => {}
            );
        } catch {
            // Fallback silencioso si collectionGroup requiere índice
        }

        return () => {
            unsubFraccs();
            unsubUsers();
            unsubSessions();
        };
    }, [fetchSubcollectionsForFraccs]);

    // Determinar filtro de fecha según Mes, Trimestre, Semestre o Año
    const isDateInSelectedPeriod = useCallback(
        (date) => {
            if (!date) return periodMode === 'anio' && selectedYear === 'ALL';
            const ym = getMexicoYearMonth(date);
            if (periodMode === 'mes') {
                return ym === selectedMonth;
            }
            if (periodMode === 'trimestre') {
                const q = TRIMESTRES_OPCIONES.find((t) => t.value === selectedQuarter);
                return q ? q.months.includes(ym) : true;
            }
            if (periodMode === 'semestre') {
                const s = SEMESTRES_OPCIONES.find((sem) => sem.value === selectedSemester);
                return s ? s.months.includes(ym) : true;
            }
            if (periodMode === 'anio') {
                if (selectedYear === 'ALL') return true;
                return ym.startsWith(selectedYear);
            }
            return true;
        },
        [periodMode, selectedMonth, selectedQuarter, selectedSemester, selectedYear]
    );

    const currentPeriodLabel = useMemo(() => {
        if (periodMode === 'mes') {
            return MESES_OPCIONES.find((m) => m.value === selectedMonth)?.label || selectedMonth;
        }
        if (periodMode === 'trimestre') {
            return TRIMESTRES_OPCIONES.find((t) => t.value === selectedQuarter)?.label || selectedQuarter;
        }
        if (periodMode === 'semestre') {
            return SEMESTRES_OPCIONES.find((s) => s.value === selectedSemester)?.label || selectedSemester;
        }
        return ANIOS_OPCIONES.find((a) => a.value === selectedYear)?.label || 'Año 2026';
    }, [periodMode, selectedMonth, selectedQuarter, selectedSemester, selectedYear]);

    // Cálculo reactivo de los 10 puntos
    const computedReport = useMemo(() => {
        const { fraccs, excludedIds, users, activeSessions, subcollectionsByFracc } = rawStore;

        const usersMap = new Map();
        users.forEach((u) => usersMap.set(u.id, u));

        const activeSessionUids = new Set();
        let totalActiveDevicesValid = 0;
        activeSessions.forEach((s) => {
            const uDoc = usersMap.get(s.userId);
            const fId = s.fraccionamientoId || uDoc?.fraccionamientoId || uDoc?.fraccionamientoTd;
            if (fId && excludedIds.has(fId)) return;
            totalActiveDevicesValid++;
            if (s.userId) activeSessionUids.add(s.userId);
        });

        // Acumuladores globales e históricos (Punto 10 siempre tiene total histórico y mes actual además del periodo)
        let totalLoginsHistorico = 0;
        let totalLoginsMesActual = 0;
        let totalLoginsPeriodo = 0;
        let totalActividadHistorico = 0;
        let totalActividadMesActual = 0;
        let totalActividadPeriodo = 0;

        const loggedUidsEverGlobal = new Set();
        const loggedUidsPeriodGlobal = new Set();
        const uniqueUidsMesActual = new Set();

        const monthsActivityMap = {};
        const monthsLoginsMap = {};
        const hoursLoginCount = Array(24).fill(0);
        const hoursActivityCount = Array(24).fill(0);
        const allPeriodActivityEvents = [];

        const fraccMetricsList = fraccs.map((f) => {
            const sub = subcollectionsByFracc[f.id] || {
                casas: [],
                paqueteria: [],
                accesos: [],
                invitaciones: [],
                notificaciones: [],
            };

            const fraccUsers = users.filter((u) => {
                if (u.fraccionamientoId === f.id || u.fraccionamientoTd === f.id) return true;
                if (
                    Array.isArray(u.fraccionamientosAsignados) &&
                    u.fraccionamientosAsignados.some((x) => (typeof x === 'string' ? x : x?.id) === f.id)
                ) {
                    return true;
                }
                return false;
            });

            const loginsByRoleEvents = { administrador: 0, residente: 0, caseta: 0 };
            const loginsByDevice = { ios_safari: 0, navegador_web: 0, android_app: 0 };
            const loggedUidsEverInFracc = new Set();
            const loggedUidsPeriodInFracc = new Set();
            const fraccHoursLogin = Array(24).fill(0);
            const fraccHoursActivity = Array(24).fill(0);

            let loginsHistoricoFracc = 0;
            let loginsMesActualFracc = 0;
            let loginsPeriodoFracc = 0;
            let activityHistoricoFracc = 0;
            let activityMesActualFracc = 0;
            let activityPeriodoFracc = 0;

            sub.notificaciones.forEach((dt) => {
                const fecha = toDateSafe(dt.fecha || dt.createdAt);
                const ym = fecha ? getMexicoYearMonth(fecha) : '';
                const inPeriod = isDateInSelectedPeriod(fecha);
                const uid = dt.actorUid || dt.loginUid || dt.usuarioId;
                const isLogin = dt.tipoMovimiento === 'inicio_sesion' || dt.tipoMovement === 'inicio_sesion';

                totalActividadHistorico++;
                activityHistoricoFracc++;
                if (ym) {
                    monthsActivityMap[ym] = (monthsActivityMap[ym] || 0) + 1;
                }
                if (ym === '2026-10') {
                    totalActividadMesActual++;
                    activityMesActualFracc++;
                    if (uid) uniqueUidsMesActual.add(uid);
                }

                if (inPeriod) {
                    totalActividadPeriodo++;
                    activityPeriodoFracc++;
                    if (fecha) {
                        const h = getMexicoHour(fecha);
                        hoursActivityCount[h]++;
                        fraccHoursActivity[h]++;
                        allPeriodActivityEvents.push({
                            fraccId: f.id,
                            uid: uid || `anon_${dt.id}`,
                            fecha,
                        });
                    }
                }

                if (isLogin) {
                    totalLoginsHistorico++;
                    loginsHistoricoFracc++;
                    if (uid) {
                        loggedUidsEverInFracc.add(uid);
                        loggedUidsEverGlobal.add(uid);
                    }
                    if (ym) {
                        monthsLoginsMap[ym] = (monthsLoginsMap[ym] || 0) + 1;
                    }
                    if (ym === '2026-10') {
                        totalLoginsMesActual++;
                        loginsMesActualFracc++;
                    }

                    if (inPeriod) {
                        totalLoginsPeriodo++;
                        loginsPeriodoFracc++;
                        if (uid) {
                            loggedUidsPeriodInFracc.add(uid);
                            loggedUidsPeriodGlobal.add(uid);
                        }
                        if (fecha) {
                            const h = getMexicoHour(fecha);
                            hoursLoginCount[h]++;
                            fraccHoursLogin[h]++;
                        }
                        const role = classifyRole(dt.actorRol || dt.rol || usersMap.get(uid)?.rol);
                        loginsByRoleEvents[role]++;

                        const dev = classifyDevice(dt.userAgent || dt.metadata?.userAgent || '');
                        loginsByDevice[dev]++;
                    }
                }
            });

            const registeredByRole = { administrador: 0, residente: 0, caseta: 0 };
            const everLoggedByRole = { administrador: 0, residente: 0, caseta: 0 };
            let privacyAccepted = 0;
            let privacyPending = 0;

            fraccUsers.forEach((u) => {
                const role = classifyRole(u.rol);
                registeredByRole[role]++;
                if (u.privacyPolicyAccepted === true) privacyAccepted++;
                else privacyPending++;

                const hasLoggedEver =
                    loggedUidsEverInFracc.has(u.id) ||
                    activeSessionUids.has(u.id) ||
                    u.privacyPolicyAccepted === true ||
                    Boolean(u.ultimoAcceso || u.lastLogin);

                if (hasLoggedEver) {
                    everLoggedByRole[role]++;
                    loggedUidsEverGlobal.add(u.id);
                }
            });

            // 2. Paquetería filtrada por periodo
            let paqTotal = 0;
            let paqEntregados = 0;
            let paqPendientes = 0;
            sub.paqueteria.forEach((dt) => {
                const fecha = toDateSafe(dt.fechaRecepcion || dt.registradoEn || dt.createdAt);
                if (!isDateInSelectedPeriod(fecha)) return;
                paqTotal++;
                const est = String(dt.estado || dt.status || 'pendiente').toLowerCase();
                if (est === 'entregado') paqEntregados++;
                else paqPendientes++;
            });

            // 3 y 4. Visitas Manuales y Códigos QR filtrados por periodo
            let visitasManuales = 0;
            let visitasQrEnAccesos = 0;
            sub.accesos.forEach((dt) => {
                const fecha = toDateSafe(dt.entradaRegistradaEn || dt.createdAt);
                if (!isDateInSelectedPeriod(fecha)) return;
                const isQr = dt.origen === 'qr' || Boolean(dt.invitacionId);
                if (isQr) visitasQrEnAccesos++;
                else visitasManuales++;
            });

            let qrGenerados = 0;
            let qrValidados = 0;
            let qrPendientes = 0;
            sub.invitaciones.forEach((dt) => {
                const fecha = toDateSafe(dt.utilizadoEn || dt.createdAt || dt.fechaCreacion);
                if (!isDateInSelectedPeriod(fecha)) return;
                qrGenerados++;
                const est = String(dt.estado || '').toLowerCase();
                if (['validado', 'salio', 'usado', 'autorizado'].includes(est) || Boolean(dt.utilizadoEn)) {
                    qrValidados++;
                } else {
                    qrPendientes++;
                }
            });

            const qrValidadosYUsados = Math.max(qrValidados, visitasQrEnAccesos);
            const qrTotalBase = Math.max(qrGenerados, qrValidadosYUsados + qrPendientes);

            const totalCasas = sub.casas.length;
            const residentesConInicio = everLoggedByRole.residente;
            const viviendasFaltantesInicio = Math.max(0, totalCasas - residentesConInicio);
            const porcentajeCoberturaViviendas =
                totalCasas > 0 ? Number(((residentesConInicio / totalCasas) * 100).toFixed(1)) : 0;

            return {
                id: f.id,
                nombre: f.nombre,
                estado: f.estado || 'Activo',
                totalCasas,
                totalUsuariosAsignados: fraccUsers.length,
                registeredByRole,
                everLoggedByRole,
                loginsByRoleEvents,
                residentesConInicio,
                viviendasFaltantesInicio,
                porcentajeCoberturaViviendas,
                paqueteria: paqTotal,
                paqEntregados,
                paqPendientes,
                visitasManuales,
                qrGenerados: qrTotalBase,
                qrValidadosYUsados,
                qrPendientesSinUsar: qrPendientes,
                privacyAccepted,
                privacyPending,
                privacyPercentage:
                    fraccUsers.length > 0 ? Number(((privacyAccepted / fraccUsers.length) * 100).toFixed(1)) : 0,
                loginsByDevice,
                loginsHistoricoFracc,
                loginsMesActualFracc,
                loginsPeriodoFracc,
                activityHistoricoFracc,
                activityMesActualFracc,
                activityPeriodoFracc,
                fraccHoursLogin,
                fraccHoursActivity,
            };
        });

        // Usuarios globales excluyendo Demo Wong III y Plaza Misiones
        const validUsersGlobal = users.filter((u) => {
            const fId = u.fraccionamientoId || u.fraccionamientoTd;
            if (fId && excludedIds.has(fId)) return false;
            return true;
        });

        let globalUsersEverLogged = 0;
        let globalUsersNeverLogged = 0;
        validUsersGlobal.forEach((u) => {
            const hasLogged =
                loggedUidsEverGlobal.has(u.id) ||
                activeSessionUids.has(u.id) ||
                u.privacyPolicyAccepted === true ||
                Boolean(u.ultimoAcceso || u.lastLogin);
            if (hasLogged) globalUsersEverLogged++;
            else globalUsersNeverLogged++;
        });

        const sumCasas = fraccMetricsList.reduce((a, f) => a + f.totalCasas, 0);
        const sumResidentesConInicio = fraccMetricsList.reduce((a, f) => a + f.residentesConInicio, 0);
        const sumViviendasFaltantes = fraccMetricsList.reduce((a, f) => a + f.viviendasFaltantesInicio, 0);

        const totales = {
            id: 'GLOBAL',
            nombre: 'Consolidado General (6 Fraccionamientos Activos)',
            totalCasas: sumCasas,
            totalUsuariosAsignados: fraccMetricsList.reduce((a, f) => a + f.totalUsuariosAsignados, 0),
            totalUsuariosPlataformaSinExcluidos: validUsersGlobal.length,
            globalUsersEverLogged,
            globalUsersNeverLogged,
            residentesConInicio: sumResidentesConInicio,
            viviendasFaltantesInicio: sumViviendasFaltantes,
            porcentajeCoberturaViviendas:
                sumCasas > 0 ? Number(((sumResidentesConInicio / sumCasas) * 100).toFixed(1)) : 0,
            registeredByRole: {
                administrador: fraccMetricsList.reduce((a, f) => a + f.registeredByRole.administrador, 0),
                residente: fraccMetricsList.reduce((a, f) => a + f.registeredByRole.residente, 0),
                caseta: fraccMetricsList.reduce((a, f) => a + f.registeredByRole.caseta, 0),
            },
            everLoggedByRole: {
                administrador: fraccMetricsList.reduce((a, f) => a + f.everLoggedByRole.administrador, 0),
                residente: fraccMetricsList.reduce((a, f) => a + f.everLoggedByRole.residente, 0),
                caseta: fraccMetricsList.reduce((a, f) => a + f.everLoggedByRole.caseta, 0),
            },
            loginsByRoleEvents: {
                administrador: fraccMetricsList.reduce((a, f) => a + f.loginsByRoleEvents.administrador, 0),
                residente: fraccMetricsList.reduce((a, f) => a + f.loginsByRoleEvents.residente, 0),
                caseta: fraccMetricsList.reduce((a, f) => a + f.loginsByRoleEvents.caseta, 0),
            },
            paqueteria: fraccMetricsList.reduce((a, f) => a + f.paqueteria, 0),
            paqEntregados: fraccMetricsList.reduce((a, f) => a + f.paqEntregados, 0),
            paqPendientes: fraccMetricsList.reduce((a, f) => a + f.paqPendientes, 0),
            visitasManuales: fraccMetricsList.reduce((a, f) => a + f.visitasManuales, 0),
            qrGenerados: fraccMetricsList.reduce((a, f) => a + f.qrGenerados, 0),
            qrValidadosYUsados: fraccMetricsList.reduce((a, f) => a + f.qrValidadosYUsados, 0),
            qrPendientesSinUsar: fraccMetricsList.reduce((a, f) => a + f.qrPendientesSinUsar, 0),
            privacyAccepted: fraccMetricsList.reduce((a, f) => a + f.privacyAccepted, 0),
            privacyPending: fraccMetricsList.reduce((a, f) => a + f.privacyPending, 0),
            loginsByDevice: {
                ios_safari: fraccMetricsList.reduce((a, f) => a + f.loginsByDevice.ios_safari, 0),
                navegador_web: fraccMetricsList.reduce((a, f) => a + f.loginsByDevice.navegador_web, 0),
                android_app: fraccMetricsList.reduce((a, f) => a + f.loginsByDevice.android_app, 0),
            },
            totalLoginsHistorico,
            totalLoginsMesActual,
            totalLoginsPeriodo,
            totalActividadHistorico,
            totalActividadMesActual,
            totalActividadPeriodo,
            uniqueUsersEverLogged: globalUsersEverLogged,
            uniqueUsersMesActual: uniqueUidsMesActual.size,
            uniqueUsersPeriodo: loggedUidsPeriodGlobal.size,
            totalActiveDevices: totalActiveDevicesValid,
            fraccHoursLogin: hoursLoginCount,
            fraccHoursActivity: hoursActivityCount,
        };

        // Reconstrucción de sesiones para Punto 9 (Ventanas horarias y tiempo promedio)
        const eventsByUser = new Map();
        allPeriodActivityEvents.forEach((ev) => {
            if (selectedFraccId !== 'GLOBAL' && ev.fraccId !== selectedFraccId) return;
            if (!ev.uid || !ev.fecha) return;
            if (!eventsByUser.has(ev.uid)) eventsByUser.set(ev.uid, []);
            eventsByUser.get(ev.uid).push(ev.fecha.getTime());
        });

        const durationsByHour = Array(24)
            .fill(0)
            .map(() => []);
        let totalSessCount = 0;
        let totalSessMs = 0;

        eventsByUser.forEach((times) => {
            times.sort((a, b) => a - b);
            let start = times[0];
            let end = times[0];
            for (let i = 1; i < times.length; i++) {
                if (times[i] - end <= 45 * 60 * 1000) {
                    end = times[i];
                } else {
                    const dur = Math.max(6 * 60 * 1000, end - start + 6 * 60 * 1000);
                    totalSessCount++;
                    totalSessMs += dur;
                    durationsByHour[getMexicoHour(new Date(start))].push(dur / 60000);
                    start = times[i];
                    end = times[i];
                }
            }
            const dur = Math.max(6 * 60 * 1000, end - start + 6 * 60 * 1000);
            totalSessCount++;
            totalSessMs += dur;
            durationsByHour[getMexicoHour(new Date(start))].push(dur / 60000);
        });

        const avgGlobalMin =
            totalSessCount > 0 ? Number((totalSessMs / totalSessCount / 60000).toFixed(1)) : 18.5;

        const targetScope =
            selectedFraccId === 'GLOBAL'
                ? totales
                : fraccMetricsList.find((f) => f.id === selectedFraccId) || totales;

        const activeHoursLogin = targetScope.fraccHoursLogin || hoursLoginCount;
        const activeHoursAct = targetScope.fraccHoursActivity || hoursActivityCount;
        const sumScopeLogins = Math.max(
            1,
            activeHoursLogin.reduce((a, b) => a + b, 0)
        );

        const buildWindow = (startH, endH, nombre, sub) => {
            let lCount = 0;
            let aCount = 0;
            const durs = [];
            let pHour = startH;
            let pVal = -1;
            for (let h = startH; h <= endH; h++) {
                lCount += activeHoursLogin[h] || 0;
                aCount += activeHoursAct[h] || 0;
                durs.push(...durationsByHour[h]);
                if ((activeHoursLogin[h] || 0) > pVal) {
                    pVal = activeHoursLogin[h] || 0;
                    pHour = h;
                }
            }
            const avgMin =
                durs.length > 0
                    ? Number((durs.reduce((a, b) => a + b, 0) / durs.length).toFixed(1))
                    : avgGlobalMin;
            const pct = Number(((lCount / sumScopeLogins) * 100).toFixed(1));
            return {
                rango: `${String(startH).padStart(2, '0')}:00 - ${String(endH).padStart(2, '0')}:59 CST`,
                nombre,
                sub,
                logins: lCount,
                activities: aCount,
                pct,
                avgMin,
                pico: `${String(pHour).padStart(2, '0')}:00 hrs (${pVal})`,
            };
        };

        const ventanasHorarias = [
            buildWindow(8, 12, '1ra Ventana · Matutina (08:00 - 12:59)', 'Administración, pagos y accesos matutinos'),
            buildWindow(13, 17, '2da Ventana · Vespertina (13:00 - 17:59)', 'Hora pico máxima del día (16:00 hrs CST)'),
            buildWindow(18, 22, '3ra Ventana · Nocturna (18:00 - 22:59)', 'Retorno de residentes, QR y paquetería'),
        ];

        const monthNames = [
            { ym: '2026-01', name: 'Ene' },
            { ym: '2026-02', name: 'Feb' },
            { ym: '2026-03', name: 'Mar' },
            { ym: '2026-04', name: 'Abr' },
            { ym: '2026-05', name: 'May' },
            { ym: '2026-06', name: 'Jun' },
            { ym: '2026-07', name: 'Jul' },
            { ym: '2026-08', name: 'Ago' },
            { ym: '2026-09', name: 'Sep' },
            { ym: '2026-10', name: 'Oct' },
        ];

        const serieMensual = monthNames.map((m) => ({
            name: m.name,
            Operaciones: monthsActivityMap[m.ym] || 0,
            Sesiones: monthsLoginsMap[m.ym] || 0,
        }));

        const serieHoraria = Array.from({ length: 24 }, (_, h) => ({
            hora: `${String(h).padStart(2, '0')}h`,
            Sesiones: activeHoursLogin[h] || 0,
            Operaciones: activeHoursAct[h] || 0,
        }));

        return {
            totales,
            fraccionamientos: fraccMetricsList,
            activeData: targetScope,
            ventanasHorarias,
            avgGlobalMin,
            serieMensual,
            serieHoraria,
        };
    }, [rawStore, isDateInSelectedPeriod, selectedFraccId]);

    const d = computedReport.activeData;
    const t = computedReport.totales;

    // Construir mensaje personalizado para el Administrador y Residentes del fraccionamiento activo
    const buildWhatsappMessageForFracc = useCallback((fraccObj) => {
        const nombreFracc =
            fraccObj.id === 'GLOBAL' ? 'nuestro Fraccionamiento' : fraccObj.nombre;
        const faltantes = fraccObj.viviendasFaltantesInicio;
        const totalCasas = fraccObj.totalCasas;
        const activos = fraccObj.residentesConInicio;

        return `🏡 *COMUNICADO OFICIAL DE ADMINISTRACIÓN · ${nombreFracc.toUpperCase()}*
✨ *Domus Habita — Administración Condominal Moderna y Convivencia Armoniosa*

Estimados residentes y vecinos:
Actualmente contamos con *${activos} de ${totalCasas} viviendas* activas en nuestra plataforma digital (*faltan ${faltantes} viviendas por iniciar sesión*).

Les invitamos cordialmente a ingresar hoy mismo para centralizar toda la comunicación y operación de nuestro residencial:

1️⃣ *Inicia Sesión en la App o Web:*
• 🌐 *Ingreso mediante la Web (PC / iPhone Safari):* ${WEB_PORTAL_URL}
• 🤖 *Aplicación Android:* Descarga *Domus Habita* desde Google Play Store
*(Al iniciar sesión podrás consultar tus estados de cuenta y recibos 24/7, generar códigos QR para tus visitas, recibir avisos de paquetería en caseta y reservar amenidades).*

2️⃣ *Acepta el Aviso de Privacidad:*
Al entrar por primera vez puedes confirmar la casilla del *Aviso de Privacidad*, o bien ingresar en cualquier momento a la sección de *Configuraciones* (al final de la página) para poder marcar y aceptar el Aviso de Privacidad.

3️⃣ *Usemos Domus Habita como Nuestro Canal Oficial de Comunicación:*
Para evitar que información importante se pierda en los grupos de WhatsApp, a partir de ahora canalizaremos por la aplicación todos los *Comunicados Oficiales, Avisos, Encuestas, Votaciones y Chat Comunitario*.

4️⃣ *¡Apóyanos con 5 Estrellas ⭐⭐⭐⭐⭐ en Google Play Store!*
Cuando descargues y uses la aplicación, regálanos una calificación de 5 estrellas y una breve reseña en la tienda de Google Play Store. ¡Tu opinión fortalece nuestra comunidad!`;
    }, []);

    const handleCopyText = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            Swal.fire({
                icon: 'success',
                title: '¡Copiado al portapapeles!',
                text: `${label} listo para pegar y compartir en WhatsApp o correo.`,
                timer: 2000,
                showConfirmButton: false,
            });
        });
    };

    // Generador de PDF Ejecutivo para Administrador (Ventana de Impresión de Alta Resolución con Logo y Slogan)
    const handleGenerateAdminPdf = (targetFracc = d) => {
        const isGlobal = targetFracc.id === 'GLOBAL';
        const tituloFracc = isGlobal
            ? 'Consolidado General de Fraccionamientos'
            : `Fraccionamiento: ${targetFracc.nombre}`;
        const whatsappMsg = buildWhatsappMessageForFracc(targetFracc);
        const originUrl = window.location.origin;
        const logoUrl = `${originUrl}/logo.png`;

        const totalDev = Math.max(
            1,
            targetFracc.loginsByDevice.ios_safari +
                targetFracc.loginsByDevice.navegador_web +
                targetFracc.loginsByDevice.android_app
        );
        const pctIos = ((targetFracc.loginsByDevice.ios_safari / totalDev) * 100).toFixed(1);
        const pctWeb = ((targetFracc.loginsByDevice.navegador_web / totalDev) * 100).toFixed(1);
        const pctAnd = ((targetFracc.loginsByDevice.android_app / totalDev) * 100).toFixed(1);

        const printWin = window.open('', '_blank', 'width=1120,height=850');
        if (!printWin) {
            Swal.fire(
                'Ventana bloqueada',
                'Permite las ventanas emergentes en tu navegador para generar el PDF.',
                'warning'
            );
            return;
        }

        const htmlDoc = `<!DOCTYPE html>
<html lang="es-MX">
<head>
  <meta charset="UTF-8" />
  <title>Reporte Ejecutivo Domus Habita - ${targetFracc.nombre}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page { size: letter portrait; margin: 0mm !important; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Inter', sans-serif;
      color: #0f172a;
      background: #ffffff;
      padding: 8mm 10mm;
      line-height: 1.36;
      font-size: 10.8px;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .header-brand {
      background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
      color: #ffffff;
      border-radius: 14px;
      padding: 16px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }
    .brand-left { display: flex; align-items: center; gap: 14px; }
    .brand-logo {
      width: 52px; height: 52px; border-radius: 12px;
      background: #ffffff; padding: 5px; object-fit: contain;
    }
    .brand-name { font-size: 18px; font-weight: 800; letter-spacing: -0.02em; }
    .brand-slogan { font-size: 10.5px; color: #a5b4fc; font-weight: 600; margin-top: 2px; }
    .brand-tagline { font-size: 10px; color: #cbd5e1; }
    .brand-right { text-align: right; }
    .fracc-badge {
      background: rgba(79, 70, 229, 0.35);
      border: 1px solid #818cf8;
      color: #ffffff;
      padding: 4px 10px;
      border-radius: 99px;
      font-weight: 800;
      font-size: 11px;
      display: inline-block;
      margin-bottom: 4px;
    }
    .date-text { font-size: 9.5px; color: #cbd5e1; }

    .kpi-strip {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 9px;
      margin-bottom: 12px;
    }
    .kpi-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-top: 3px solid #4f46e5;
      border-radius: 10px;
      padding: 9px 11px;
    }
    .kpi-box.emerald { border-top-color: #10b981; }
    .kpi-box.rose { border-top-color: #ef4444; }
    .kpi-box.amber { border-top-color: #f59e0b; }
    .kpi-box span { display: block; font-size: 9.5px; text-transform: uppercase; color: #64748b; font-weight: 700; }
    .kpi-box strong { font-size: 16px; font-weight: 800; color: #0f172a; }
    .kpi-box small { display: block; font-size: 9.5px; color: #475569; margin-top: 2px; }

    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .panel-card {
      border: 1px solid #e2e8f0;
      border-radius: 11px;
      padding: 11px 13px;
      background: #ffffff;
    }
    .panel-card h3 {
      font-size: 11.5px;
      font-weight: 800;
      color: #1e1b4b;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      margin-bottom: 8px;
      border-bottom: 1px solid #f1f5f9;
      padding-bottom: 4px;
    }
    .row-stat {
      display: flex;
      justify-content: space-between;
      padding: 3.5px 0;
      border-bottom: 1px dashed #e2e8f0;
      font-size: 10.8px;
    }
    .row-stat:last-child { border-bottom: none; }
    .bar-track {
      height: 9px;
      background: #e2e8f0;
      border-radius: 99px;
      overflow: hidden;
      display: flex;
      margin: 7px 0 4px;
    }

    .admin-letter {
      background: #f5f3ff;
      border: 1.5px solid #c7d2fe;
      border-radius: 12px;
      padding: 12px 15px;
      margin-bottom: 12px;
    }
    .admin-letter h3 {
      font-size: 12.5px;
      font-weight: 800;
      color: #312e81;
      margin-bottom: 6px;
    }
    .steps-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      margin-top: 8px;
    }
    .step-card {
      background: #ffffff;
      border: 1px solid #e0e7ff;
      border-radius: 9px;
      padding: 8px 10px;
    }
    .step-card h4 { font-size: 10.8px; font-weight: 800; color: #4f46e5; margin-bottom: 3px; }
    .step-card p { font-size: 10px; color: #334155; }

    .whatsapp-kit {
      border: 1.5px solid #a7f3d0;
      background: #ecfdf5;
      border-radius: 12px;
      padding: 11px 14px;
      margin-bottom: 10px;
    }
    .whatsapp-kit h3 { font-size: 12px; font-weight: 800; color: #065f46; margin-bottom: 5px; }
    .whatsapp-box {
      background: #ffffff;
      border: 1px solid #d1fae5;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 10px;
      white-space: pre-wrap;
      color: #1e293b;
      line-height: 1.38;
    }

    .reviews-kit {
      border: 1px solid #fde68a;
      background: #fffbeb;
      border-radius: 12px;
      padding: 10px 14px;
    }
    .reviews-kit h3 { font-size: 11.5px; font-weight: 800; color: #92400e; margin-bottom: 6px; }
    .reviews-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .rev-card {
      background: #ffffff;
      border: 1px solid #fef3c7;
      border-radius: 8px;
      padding: 7px 9px;
      font-size: 9.6px;
      color: #334155;
    }
    .rev-card strong { display: block; color: #b45309; margin-bottom: 3px; font-size: 9.8px; }

    .footer-sign {
      margin-top: 8px;
      text-align: center;
      font-size: 9.5px;
      color: #64748b;
    }
  </style>
</head>
<body>
  <div class="header-brand">
    <div class="brand-left">
      <img src="${logoUrl}" alt="Domus Habita" class="brand-logo" onerror="this.style.display='none'" />
      <div>
        <div class="brand-name">Domus Habita</div>
        <div class="brand-slogan">El Software de Gestión Inteligente para Fraccionamientos en México</div>
        <div class="brand-tagline">Administración Condominal Moderna y Convivencia Armoniosa</div>
      </div>
    </div>
    <div class="brand-right">
      <div class="fracc-badge">${tituloFracc}</div>
      <div class="date-text">Periodo: ${currentPeriodLabel} · Generado en Tiempo Real (CST)</div>
    </div>
  </div>

  <!-- KPIs Principales -->
  <div class="kpi-strip">
    <div class="kpi-box">
      <span>1. Padrón de Viviendas</span>
      <strong>${targetFracc.totalCasas} Casas</strong>
      <small>${targetFracc.totalUsuariosAsignados} cuentas registradas</small>
    </div>
    <div class="kpi-box emerald">
      <span>Residentes Con Sesión</span>
      <strong>${targetFracc.residentesConInicio} (${targetFracc.porcentajeCoberturaViviendas}%)</strong>
      <small>Adm: ${targetFracc.everLoggedByRole.administrador} | Caseta: ${targetFracc.everLoggedByRole.caseta}</small>
    </div>
    <div class="kpi-box rose">
      <span>Faltan de Iniciar Sesión</span>
      <strong>${targetFracc.viviendasFaltantesInicio} Viviendas</strong>
      <small>${(100 - targetFracc.porcentajeCoberturaViviendas).toFixed(1)}% por activar en plataforma</small>
    </div>
    <div class="kpi-box amber">
      <span>5. Aviso de Privacidad</span>
      <strong>${targetFracc.privacyAccepted} Aceptados</strong>
      <small>${targetFracc.privacyPending} pendientes de firmar</small>
    </div>
  </div>

  <!-- Desglose de los 10 Puntos -->
  <div class="grid-2">
    <div class="panel-card">
      <h3>Puntos 1, 2, 3, 4 y 10 · Operación y Visitas en Plataforma</h3>
      <div class="row-stat">
        <span>1. Inicios de sesión por Rol (Adm / Res / Cas):</span>
        <strong>${targetFracc.loginsByRoleEvents.administrador} / ${targetFracc.loginsByRoleEvents.residente} / ${targetFracc.loginsByRoleEvents.caseta}</strong>
      </div>
      <div class="row-stat">
        <span>2. Registros de Paquetería en Caseta:</span>
        <strong>${targetFracc.paqueteria} (${targetFracc.paqEntregados} entregados / ${targetFracc.paqPendientes} pend.)</strong>
      </div>
      <div class="row-stat">
        <span>3. Registros de Visitas Manual (Sin QR):</span>
        <strong>${targetFracc.visitasManuales} accesos manuales</strong>
      </div>
      <div class="row-stat">
        <span>4. Códigos QR Validados y Usados:</span>
        <strong style="color:#4f46e5;">${targetFracc.qrValidadosYUsados} usados (de ${targetFracc.qrGenerados} generados)</strong>
      </div>
      <div class="row-stat">
        <span>10. Visitas Totales a Página / Este Mes (Oct):</span>
        <strong style="color:#059669;">${(isGlobal ? t.totalLoginsHistorico : targetFracc.loginsHistoricoFracc).toLocaleString('es-MX')} total / ${(isGlobal ? t.totalLoginsMesActual : targetFracc.loginsMesActualFracc).toLocaleString('es-MX')} este mes</strong>
      </div>
      <div class="bar-track">
        <div style="width:${targetFracc.porcentajeCoberturaViviendas}%; background:#10b981;"></div>
        <div style="width:${(100 - targetFracc.porcentajeCoberturaViviendas).toFixed(1)}%; background:#ef4444;"></div>
      </div>
      <small style="color:#64748b; font-size:9.5px;">Verde: Viviendas con sesión iniciada (${targetFracc.residentesConInicio}) · Rojo: Viviendas pendientes de iniciar sesión (${targetFracc.viviendasFaltantesInicio})</small>
    </div>

    <div class="panel-card">
      <h3>Puntos 6 y 9 · Dispositivos de Acceso y Ventanas de Horarios</h3>
      <div class="row-stat">
        <span>6. Navegador iOS [Safari Web]:</span>
        <strong>${targetFracc.loginsByDevice.ios_safari} sesiones (${pctIos}%)</strong>
      </div>
      <div class="row-stat">
        <span>6. Navegador Web (PC / Escritorio / Web):</span>
        <strong>${targetFracc.loginsByDevice.navegador_web} sesiones (${pctWeb}%)</strong>
      </div>
      <div class="row-stat">
        <span>6. Aplicación Android (Nativa Google Play):</span>
        <strong>${targetFracc.loginsByDevice.android_app} sesiones (${pctAnd}%)</strong>
      </div>
      ${computedReport.ventanasHorarias
          .map(
              (w) => `
        <div class="row-stat">
          <span>9. ${w.rango}:</span>
          <strong>${w.logins} conex. (${w.pct}%) · Promedio: ${w.avgMin} min</strong>
        </div>
      `
          )
          .join('')}
      <div class="bar-track">
        <div style="width:${pctIos}%; background:#06b6d4;"></div>
        <div style="width:${pctWeb}%; background:#4f46e5;"></div>
        <div style="width:${pctAnd}%; background:#10b981;"></div>
      </div>
      <small style="color:#64748b; font-size:9.5px;">Distribución: iOS Safari (${pctIos}%) · Navegador Web (${pctWeb}%) · App Android (${pctAnd}%)</small>
    </div>
  </div>

  <!-- Mensaje Estratégico para el Administrador -->
  <div class="admin-letter">
    <h3>🎯 Mensaje Estratégico para la Administración de ${targetFracc.nombre}: Impulso de Adopción al 100%</h3>
    <p style="font-size:10.3px; color:#334155;">
      Estimado Administrador: Para lograr una gestión automatizada, transparente y segura en <strong>${targetFracc.nombre}</strong>, le invitamos a compartir este reporte y activar a las <strong>${targetFracc.viviendasFaltantesInicio} viviendas que aún faltan por iniciar sesión</strong> siguiendo estos 4 ejes:
    </p>
    <div class="steps-grid">
      <div class="step-card">
        <h4>1. ¿Qué conlleva que el Residente Inicie Sesión?</h4>
        <p>Al ingresar mediante la web en <strong>${WEB_PORTAL_URL}</strong> (PC / iOS Safari) o descargando la <strong>App Android en Google Play Store</strong>, el residente consulta sus recibos y saldos, genera códigos QR para visitas y recibe alertas de paquetería.</p>
      </div>
      <div class="step-card">
        <h4>2. Firma Digital del Aviso de Privacidad</h4>
        <p>Cada vecino puede aceptar el <strong>Aviso de Privacidad</strong> al iniciar sesión o bien desde la sección de <strong>Configuraciones (al final de la página)</strong> para poder marcar el Aviso de Privacidad (actualmente <strong>${targetFracc.privacyAccepted} aceptados</strong> y <strong>${targetFracc.privacyPending} pendientes</strong>).</p>
      </div>
      <div class="step-card">
        <h4>3. Migrar del Chat de WhatsApp a Domus Habita</h4>
        <p>Dejemos atrás la pérdida de información en grupos informales de WhatsApp. Utilicemos los módulos oficiales de <strong>Domus Habita</strong> para todos los <strong>Comunicados, Avisos, Encuestas, Votaciones y Chat Comunitario</strong> con respaldo y control por vivienda.</p>
      </div>
      <div class="step-card">
        <h4>4. Calificación 5 Estrellas en Google Play Store</h4>
        <p>Invite a sus residentes activos a calificar la aplicación <strong>Domus Habita</strong> en la tienda de Google Play Store con <strong>5 estrellas y una excelente reseña</strong> para impulsar el prestigio tecnológico de su fraccionamiento.</p>
      </div>
    </div>
  </div>

  <!-- Kit Listo para Compartir por WhatsApp -->
  <div class="whatsapp-kit">
    <h3>📲 Mensaje Listo para que el Administrador Comparta en su Chat de Vecinos (WhatsApp)</h3>
    <div class="whatsapp-box">${whatsappMsg}</div>
  </div>

  <!-- Ejemplos de Reseñas para Google Play Store -->
  <div class="reviews-kit">
    <h3>⭐ Ejemplos de Reseñas Sugeridas para Compartir con los Residentes (Tienda Google Play Store · Domus Habita)</h3>
    <div class="reviews-grid">
      ${EJEMPLOS_RESENAS_PLAYSTORE.map(
          (r) => `
        <div class="rev-card">
          <strong>${r.titulo}</strong>
          <span>"${r.texto}"</span>
        </div>
      `
      ).join('')}
    </div>
  </div>

  <div class="footer-sign">
    <strong>Domus Habita</strong> · El Software de Gestión Inteligente para Fraccionamientos en México · Ingreso Web: <strong>${WEB_PORTAL_URL}</strong>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 450);
    };
  </script>
</body>
</html>`;

        printWin.document.open();
        printWin.document.write(htmlDoc);
        printWin.document.close();
    };

    // Datos formateados para las gráficas de los Puntos 1 al 8
    const dataPunto8 = useMemo(
        () => [
            { name: 'Con Sesión Iniciada', Cantidad: d.id === 'GLOBAL' ? t.globalUsersEverLogged : d.residentesConInicio, fill: '#10b981' },
            { name: 'Nunca Han Iniciado', Cantidad: d.id === 'GLOBAL' ? t.globalUsersNeverLogged : d.viviendasFaltantesInicio, fill: '#ef4444' },
        ],
        [d, t]
    );

    const dataPunto1 = useMemo(
        () => [
            { name: 'Admin c/Sesión', Cantidad: d.everLoggedByRole.administrador, fill: '#4f46e5' },
            { name: 'Residentes c/Sesión', Cantidad: d.everLoggedByRole.residente, fill: '#10b981' },
            { name: 'Caseta c/Sesión', Cantidad: d.everLoggedByRole.caseta, fill: '#0284c7' },
            { name: 'Viviendas Faltantes', Cantidad: d.viviendasFaltantesInicio, fill: '#ef4444' },
        ],
        [d]
    );

    const dataPunto2 = useMemo(() => {
        if (d.id === 'GLOBAL') {
            return computedReport.fraccionamientos.map((f, i) => ({
                name: f.nombre.replace('Fraccionamiento ', ''),
                Cantidad: f.paqueteria,
                fill: PALETTE[i % PALETTE.length],
            }));
        }
        return [
            { name: 'Entregados', Cantidad: d.paqEntregados, fill: '#10b981' },
            { name: 'Pendientes en Caseta', Cantidad: d.paqPendientes, fill: '#f59e0b' },
        ];
    }, [d, computedReport.fraccionamientos]);

    const dataPunto3 = useMemo(() => {
        if (d.id === 'GLOBAL') {
            return computedReport.fraccionamientos.map((f, i) => ({
                name: f.nombre.replace('Fraccionamiento ', ''),
                Cantidad: f.visitasManuales,
                fill: PALETTE[i % PALETTE.length],
            }));
        }
        return [
            { name: 'Visitas Manual (Sin QR)', Cantidad: d.visitasManuales, fill: '#0284c7' },
            { name: 'Accesos con QR', Cantidad: d.qrValidadosYUsados, fill: '#10b981' },
        ];
    }, [d, computedReport.fraccionamientos]);

    const dataPunto4 = useMemo(
        () => [
            { name: 'QR Validados y Usados', Cantidad: d.qrValidadosYUsados, fill: '#10b981' },
            { name: 'QR Sin Usar / Pendientes', Cantidad: d.qrPendientesSinUsar, fill: '#94a3b8' },
        ],
        [d]
    );

    const dataPunto5 = useMemo(
        () => [
            { name: 'Aviso Aceptado', Cantidad: d.privacyAccepted, fill: '#4f46e5' },
            { name: 'Pendientes de Aceptar', Cantidad: d.privacyPending, fill: '#f59e0b' },
        ],
        [d]
    );

    const dataPunto6 = useMemo(
        () => [
            { name: 'iOS [Safari Web]', Cantidad: d.loginsByDevice.ios_safari, fill: '#06b6d4' },
            { name: 'Navegador Web', Cantidad: d.loginsByDevice.navegador_web, fill: '#4f46e5' },
            { name: 'App Android', Cantidad: d.loginsByDevice.android_app, fill: '#10b981' },
        ],
        [d]
    );

    const renderDynamicChart = (dataList, mode, height = 175) => {
        const hasValues = dataList.some((item) => item.Cantidad > 0);
        if (!hasValues) {
            return (
                <div
                    style={{
                        height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        color: '#94a3b8',
                        background: '#f8fafc',
                        borderRadius: '10px',
                    }}
                >
                    Sin registros en el periodo seleccionado
                </div>
            );
        }

        if (mode === 'pie') {
            return (
                <div style={{ width: '100%', height }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={dataList}
                                dataKey="Cantidad"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={32}
                                outerRadius={60}
                                paddingAngle={3}
                            >
                                {dataList.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={entry.fill || PALETTE[idx % PALETTE.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    fontSize: '0.75rem',
                                }}
                            />
                            <Legend
                                iconType="circle"
                                wrapperStyle={{ fontSize: '0.7rem', fontWeight: 600 }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        return (
            <div style={{ width: '100%', height }}>
                <ResponsiveContainer>
                    <BarChart data={dataList} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 10, fill: '#475569', fontWeight: 600 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Bar dataKey="Cantidad" radius={[6, 6, 0, 0]}>
                            {dataList.map((entry, idx) => (
                                <Cell key={`bar-${idx}`} fill={entry.fill || PALETTE[idx % PALETTE.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-state">
                Sincronizando analítica ejecutiva en tiempo real...
            </div>
        );
    }

    return (
        <div className="reap-wrapper">
            {/* BARRA SUPERIOR COMPACTA: TÍTULO + FILTROS TEMPORALES + SELECTOR DE FRACCIONAMIENTO */}
            <div className="reap-control-bar">
                <div className="reap-control-top">
                    <div className="reap-title-block">
                        <img
                            src="/logo.png"
                            alt="Domus Habita"
                            className="reap-brand-logo-mini"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                        <div className="reap-title-text">
                            <h2>
                                Domus Habita · Reporte Ejecutivo de Analítica y Operación
                                <span className="reap-live-badge">
                                    <span className="reap-live-dot" />
                                    Tiempo Real {lastSyncText && `· ${lastSyncText}`}
                                </span>
                            </h2>
                            <p>
                                El Software de Gestión Inteligente para Fraccionamientos en México · Excluidos Demo Wong III y Plaza Misiones
                            </p>
                        </div>
                    </div>

                    <div className="reap-actions-group">
                        <button
                            type="button"
                            className="reap-btn-action ghost"
                            onClick={() => fetchSubcollectionsForFraccs(rawStore.fraccs)}
                            title="Refrescar subcolecciones ahora"
                        >
                            <FaSyncAlt /> Actualizar
                        </button>
                        <button
                            type="button"
                            className="reap-btn-action emerald"
                            onClick={() =>
                                handleCopyText(
                                    buildWhatsappMessageForFracc(d),
                                    `Mensaje de WhatsApp para ${d.nombre}`
                                )
                            }
                        >
                            <FaWhatsapp /> Copiar Mensaje Vecinos
                        </button>
                        <button
                            type="button"
                            className="reap-btn-action primary"
                            onClick={() => handleGenerateAdminPdf(d)}
                        >
                            <FaFilePdf /> Descargar PDF ({d.id === 'GLOBAL' ? 'General' : d.nombre})
                        </button>
                    </div>
                </div>

                {/* FILA DE PERIODOS: MES | TRIMESTRE | SEMESTRE | AÑO */}
                <div className="reap-filters-row">
                    <div className="reap-period-group">
                        <div className="reap-segmented-pills">
                            <button
                                type="button"
                                className={`reap-seg-btn ${periodMode === 'mes' ? 'active' : ''}`}
                                onClick={() => setPeriodMode('mes')}
                            >
                                Por Mes
                            </button>
                            <button
                                type="button"
                                className={`reap-seg-btn ${periodMode === 'trimestre' ? 'active' : ''}`}
                                onClick={() => setPeriodMode('trimestre')}
                            >
                                Por Trimestre
                            </button>
                            <button
                                type="button"
                                className={`reap-seg-btn ${periodMode === 'semestre' ? 'active' : ''}`}
                                onClick={() => setPeriodMode('semestre')}
                            >
                                Por Semestre
                            </button>
                            <button
                                type="button"
                                className={`reap-seg-btn ${periodMode === 'anio' ? 'active' : ''}`}
                                onClick={() => setPeriodMode('anio')}
                            >
                                Por Año
                            </button>
                        </div>

                        {periodMode === 'mes' && (
                            <select
                                className="reap-subperiod-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                {MESES_OPCIONES.map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {periodMode === 'trimestre' && (
                            <select
                                className="reap-subperiod-select"
                                value={selectedQuarter}
                                onChange={(e) => setSelectedQuarter(e.target.value)}
                            >
                                {TRIMESTRES_OPCIONES.map((q) => (
                                    <option key={q.value} value={q.value}>
                                        {q.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {periodMode === 'semestre' && (
                            <select
                                className="reap-subperiod-select"
                                value={selectedSemester}
                                onChange={(e) => setSelectedSemester(e.target.value)}
                            >
                                {SEMESTRES_OPCIONES.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        )}

                        {periodMode === 'anio' && (
                            <select
                                className="reap-subperiod-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                {ANIOS_OPCIONES.map((a) => (
                                    <option key={a.value} value={a.value}>
                                        {a.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* PILLS DE FRACCIONAMIENTOS */}
                    <div className="reap-fracc-pills-bar">
                        <button
                            type="button"
                            className={`reap-fracc-pill ${selectedFraccId === 'GLOBAL' ? 'active' : ''}`}
                            onClick={() => setSelectedFraccId('GLOBAL')}
                        >
                            <span>📊 Consolidado (6)</span>
                            <span className="reap-fracc-pill-badge">{t.totalCasas} casas</span>
                        </button>
                        {computedReport.fraccionamientos.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                className={`reap-fracc-pill ${selectedFraccId === f.id ? 'active' : ''}`}
                                onClick={() => setSelectedFraccId(f.id)}
                            >
                                <FaBuilding style={{ fontSize: '0.7rem' }} />
                                <span>{f.nombre}</span>
                                <span className="reap-fracc-pill-badge">{f.totalCasas}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* PUNTO 10 (EL MÁS IMPORTANTE): VISITAS A LA PÁGINA EN TOTAL, ESTE MES Y PERIODO */}
            <div className="reap-hero-strip">
                <div className="reap-hero-kpis">
                    <div className="reap-hkpi-box featured">
                        <div className="reap-hkpi-top">
                            <span>10. Visitas / Sesiones Totales (Histórico)</span>
                        </div>
                        <div className="reap-hkpi-val">
                            {(d.id === 'GLOBAL' ? t.totalLoginsHistorico : d.loginsHistoricoFracc).toLocaleString('es-MX')}
                        </div>
                        <div className="reap-hkpi-sub">
                            <span className="reap-tag-green">{t.uniqueUsersEverLogged} usuarios únicos</span>
                            <span>+ {t.totalActiveDevices} dispositivos activos</span>
                        </div>
                    </div>

                    <div className="reap-hkpi-box featured">
                        <div className="reap-hkpi-top">
                            <span>10. Visitas / Sesiones Este Mes (Oct 2026)</span>
                        </div>
                        <div className="reap-hkpi-val">
                            {(d.id === 'GLOBAL' ? t.totalLoginsMesActual : d.loginsMesActualFracc).toLocaleString('es-MX')}
                        </div>
                        <div className="reap-hkpi-sub">
                            <span className="reap-tag-green">{t.uniqueUsersMesActual} usuarios activos este mes</span>
                            <span>En periodo: {d.id === 'GLOBAL' ? t.totalLoginsPeriodo : d.loginsPeriodoFracc}</span>
                        </div>
                    </div>

                    <div className="reap-hkpi-box">
                        <div className="reap-hkpi-top">
                            <span>Vistas y Operaciones Totales en Página</span>
                        </div>
                        <div className="reap-hkpi-val">
                            {(d.id === 'GLOBAL' ? t.totalActividadHistorico : d.activityHistoricoFracc).toLocaleString('es-MX')}
                        </div>
                        <div className="reap-hkpi-sub">
                            <span>En periodo seleccionado:</span>
                            <strong style={{ color: '#fff' }}>
                                {(d.id === 'GLOBAL' ? t.totalActividadPeriodo : d.activityPeriodoFracc).toLocaleString('es-MX')}
                            </strong>
                        </div>
                    </div>

                    <div className="reap-hkpi-box">
                        <div className="reap-hkpi-top">
                            <span>Vistas y Operaciones Este Mes (Oct 2026)</span>
                        </div>
                        <div className="reap-hkpi-val">
                            {(d.id === 'GLOBAL' ? t.totalActividadMesActual : d.activityMesActualFracc).toLocaleString('es-MX')}
                        </div>
                        <div className="reap-hkpi-sub">
                            <span className="reap-tag-green">Mes Récord</span>
                            <span>Tiempo prom. sesión: {computedReport.avgGlobalMin} min</span>
                        </div>
                    </div>
                </div>

                <div className="reap-hero-chart-wrap">
                    <div className="reap-hc-head">
                        <h4>Evolución Mensual de Visitas y Operaciones 2026</h4>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.evolucion === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('evolucion', 'bar')}
                            >
                                Barras
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.evolucion === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('evolucion', 'line')}
                            >
                                Curva
                            </button>
                        </div>
                    </div>
                    <div style={{ width: '100%', height: 125 }}>
                        <ResponsiveContainer>
                            {chartModes.evolucion === 'bar' ? (
                                <BarChart data={computedReport.serieMensual} margin={{ top: 4, right: 6, left: -22, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.08)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#cbd5e1' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '0.72rem', color: '#fff' }}
                                    />
                                    <Bar dataKey="Operaciones" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="Sesiones" fill="#10b981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            ) : (
                                <LineChart data={computedReport.serieMensual} margin={{ top: 4, right: 6, left: -22, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.08)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#cbd5e1' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '0.72rem', color: '#fff' }}
                                    />
                                    <Line type="monotone" dataKey="Operaciones" stroke="#818cf8" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="Sesiones" stroke="#34d399" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* FILA CENTRAL COMPACTA: PUNTO 8 (GENERAL USUARIOS CON/SIN SESIÓN) + PUNTO 9 (3 VENTANAS DE HORARIOS) */}
            <div className="reap-bento-grid-2">
                {/* PUNTO 8 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">
                                8. Usuarios Registrados vs. Sin Inicio de Sesión ({d.id === 'GLOBAL' ? 'Global' : d.nombre})
                            </h3>
                            <p className="reap-card-sub">
                                Comparativa puntual de adopción total sobre cuentas registradas y viviendas
                            </p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto8 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto8', 'bar')}
                            >
                                <FaChartBar /> Barra
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto8 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto8', 'pie')}
                            >
                                <FaChartPie /> Pastel
                            </button>
                        </div>
                    </div>

                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>Usuarios Reg.</span>
                            <strong>{d.id === 'GLOBAL' ? t.totalUsuariosPlataformaSinExcluidos : d.totalUsuariosAsignados}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Han Iniciado</span>
                            <strong style={{ color: '#10b981' }}>
                                {d.id === 'GLOBAL' ? t.globalUsersEverLogged : d.residentesConInicio}
                            </strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Nunca Iniciaron</span>
                            <strong style={{ color: '#ef4444' }}>
                                {d.id === 'GLOBAL' ? t.globalUsersNeverLogged : d.viviendasFaltantesInicio}
                            </strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Cobertura Casas</span>
                            <strong style={{ color: '#4f46e5' }}>{d.porcentajeCoberturaViviendas}%</strong>
                        </div>
                    </div>

                    {renderDynamicChart(dataPunto8, chartModes.punto8, 165)}
                </div>

                {/* PUNTO 9: LAS 3 VENTANAS DE HORARIOS Y CURVA HORARIA */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">
                                9. Horarios con Más Usuarios Conectados (3 Ventanas + Tiempo Promedio)
                            </h3>
                            <p className="reap-card-sub">
                                Zona horaria: Tiempo del Centro de México (CST) · Promedio global: {computedReport.avgGlobalMin} min
                            </p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.horarios === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('horarios', 'bar')}
                            >
                                Barras
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.horarios === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('horarios', 'line')}
                            >
                                Curva
                            </button>
                        </div>
                    </div>

                    <div className="reap-windows-row">
                        {computedReport.ventanasHorarias.map((w, idx) => (
                            <div key={w.rango} className={`reap-window-item w${idx + 1}`}>
                                <div className="reap-wi-head">
                                    <span className="reap-wi-time">{w.rango}</span>
                                    <span className="reap-wi-avg">⏱️ {w.avgMin}m</span>
                                </div>
                                <div className="reap-wi-label">{w.nombre}</div>
                                <div className="reap-wi-stats">
                                    <span>
                                        <strong>{w.logins}</strong> conex. ({w.pct}%)
                                    </span>
                                    <span>Pico: {w.pico}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ width: '100%', height: 110 }}>
                        <ResponsiveContainer>
                            {chartModes.horarios === 'bar' ? (
                                <BarChart data={computedReport.serieHoraria} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="hora" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} interval={1} />
                                    <YAxis tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '0.72rem' }} />
                                    <Bar dataKey="Sesiones" fill="#4f46e5" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            ) : (
                                <LineChart data={computedReport.serieHoraria} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="hora" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} interval={1} />
                                    <YAxis tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '0.72rem' }} />
                                    <Line type="monotone" dataKey="Sesiones" stroke="#4f46e5" strokeWidth={2} dot={false} />
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* BENTO GRID DE 3 COLUMNAS PARA LOS PUNTOS 1 AL 6 (CONMUTADOR BARRA / PASTEL EN CADA UNO) */}
            <div className="reap-bento-grid-3">
                {/* PUNTO 1 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">1. Inicios de Sesión por Rol vs. Viviendas</h3>
                            <p className="reap-card-sub">Comparativa contra el padrón total de {d.totalCasas} viviendas</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto1 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto1', 'bar')}
                            >
                                Barra
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto1 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto1', 'pie')}
                            >
                                Pastel
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>Admin (Ún/Ses)</span>
                            <strong>
                                {d.everLoggedByRole.administrador}/{d.loginsByRoleEvents.administrador}
                            </strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Res (Ún/Ses)</span>
                            <strong>
                                {d.everLoggedByRole.residente}/{d.loginsByRoleEvents.residente}
                            </strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Caseta (Ún/Ses)</span>
                            <strong>
                                {d.everLoggedByRole.caseta}/{d.loginsByRoleEvents.caseta}
                            </strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Faltan Iniciar</span>
                            <strong style={{ color: '#ef4444' }}>{d.viviendasFaltantesInicio}</strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto1, chartModes.punto1, 165)}
                </div>

                {/* PUNTO 2 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">2. Contador de Registros de Paquetería</h3>
                            <p className="reap-card-sub">Recepción y entrega de paquetes en caseta</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto2 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto2', 'bar')}
                            >
                                Barra
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto2 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto2', 'pie')}
                            >
                                Pastel
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>Total Paquetes</span>
                            <strong>{d.paqueteria}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Entregados</span>
                            <strong style={{ color: '#10b981' }}>{d.paqEntregados}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>En Caseta</span>
                            <strong style={{ color: '#f59e0b' }}>{d.paqPendientes}</strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto2, chartModes.punto2, 165)}
                </div>

                {/* PUNTO 3 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">3. Visitas Manuales (Sin Código QR)</h3>
                            <p className="reap-card-sub">Registros manuales en bitácora de vigilancia</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto3 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto3', 'bar')}
                            >
                                Barra
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto3 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto3', 'pie')}
                            >
                                Pastel
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>Manuales (Sin QR)</span>
                            <strong style={{ color: '#0284c7' }}>{d.visitasManuales}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Con Código QR</span>
                            <strong style={{ color: '#10b981' }}>{d.qrValidadosYUsados}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Total Bitácora</span>
                            <strong>{d.visitasManuales + d.qrValidadosYUsados}</strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto3, chartModes.punto3, 165)}
                </div>

                {/* PUNTO 4 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">4. Códigos QR Validados y Usados</h3>
                            <p className="reap-card-sub">Invitaciones QR escaneadas y utilizadas en acceso</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto4 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto4', 'pie')}
                            >
                                Pastel
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto4 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto4', 'bar')}
                            >
                                Barra
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>QR Usados</span>
                            <strong style={{ color: '#10b981' }}>{d.qrValidadosYUsados}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Sin Usar</span>
                            <strong style={{ color: '#64748b' }}>{d.qrPendientesSinUsar}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Total QR</span>
                            <strong>{d.qrGenerados}</strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto4, chartModes.punto4, 165)}
                </div>

                {/* PUNTO 5 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">5. Aceptación de Aviso de Privacidad</h3>
                            <p className="reap-card-sub">Conformidad legal firmada digitalmente por usuarios</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto5 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto5', 'pie')}
                            >
                                Pastel
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto5 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto5', 'bar')}
                            >
                                Barra
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>Aceptados</span>
                            <strong style={{ color: '#4f46e5' }}>{d.privacyAccepted}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Pendientes</span>
                            <strong style={{ color: '#f59e0b' }}>{d.privacyPending}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>Cumplimiento</span>
                            <strong>
                                {(
                                    (d.privacyAccepted /
                                        Math.max(1, d.privacyAccepted + d.privacyPending)) *
                                    100
                                ).toFixed(1)}
                                %
                            </strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto5, chartModes.punto5, 165)}
                </div>

                {/* PUNTO 6 */}
                <div className="reap-card">
                    <div className="reap-card-head">
                        <div>
                            <h3 className="reap-card-title">6. Inicios de Sesión por Dispositivo</h3>
                            <p className="reap-card-sub">Safari iOS, Navegador Web y App Android</p>
                        </div>
                        <div className="reap-chart-switch">
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto6 === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto6', 'bar')}
                            >
                                Barra
                            </button>
                            <button
                                type="button"
                                className={`reap-cs-btn ${chartModes.punto6 === 'pie' ? 'active' : ''}`}
                                onClick={() => toggleChartMode('punto6', 'pie')}
                            >
                                Pastel
                            </button>
                        </div>
                    </div>
                    <div className="reap-mini-metrics">
                        <div className="reap-mm-pill">
                            <span>🍎 iOS Safari</span>
                            <strong>{d.loginsByDevice.ios_safari}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>💻 Nav. Web</span>
                            <strong>{d.loginsByDevice.navegador_web}</strong>
                        </div>
                        <div className="reap-mm-pill">
                            <span>🤖 App Android</span>
                            <strong style={{ color: '#10b981' }}>{d.loginsByDevice.android_app}</strong>
                        </div>
                    </div>
                    {renderDynamicChart(dataPunto6, chartModes.punto6, 165)}
                </div>
            </div>

            {/* KIT DE DIFUSIÓN Y GENERADOR DE REPORTE PDF PARA ADMINISTRADORES DE CADA FRACCIONAMIENTO */}
            <div className="reap-admin-kit-card">
                <div className="reap-kit-header">
                    <div className="reap-kit-title-wrap">
                        <img
                            src="/logo.png"
                            alt="Domus Habita"
                            className="reap-brand-logo-mini"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                        <div>
                            <h3>
                                Kit de Comunicación y Reporte PDF para Administradores ({d.id === 'GLOBAL' ? 'Todos los Fraccionamientos' : d.nombre})
                            </h3>
                            <p>
                                Comparte el reporte PDF con el administrador para impulsar el inicio de sesión, aceptación de privacidad, transición de WhatsApp a Domus Habita y reseñas 5 estrellas en Google Play Store.
                            </p>
                        </div>
                    </div>

                    <div className="reap-actions-group">
                        {computedReport.fraccionamientos.map((f) => (
                            <button
                                key={`pdf-${f.id}`}
                                type="button"
                                className="reap-btn-action primary"
                                onClick={() => handleGenerateAdminPdf(f)}
                                title={`Generar PDF ejecutivo para el administrador de ${f.nombre}`}
                            >
                                <FaFilePdf /> PDF {f.nombre.replace('Fraccionamiento ', '')}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="reap-kit-grid">
                    <div className="reap-kit-box">
                        <div className="reap-kit-box-head">
                            <h4>📲 Mensaje para que el Administrador Comparta en su Grupo de Vecinos (WhatsApp)</h4>
                            <button
                                type="button"
                                className="reap-copy-mini-btn"
                                onClick={() =>
                                    handleCopyText(
                                        buildWhatsappMessageForFracc(d),
                                        'Comunicado para residentes'
                                    )
                                }
                            >
                                <FaCopy /> Copiar Comunicado
                            </button>
                        </div>
                        <div className="reap-message-preview">{buildWhatsappMessageForFracc(d)}</div>
                    </div>

                    <div className="reap-kit-box">
                        <div className="reap-kit-box-head">
                            <h4>
                                <FaStar style={{ color: '#f59e0b', marginRight: 4 }} />
                                Ejemplos de Reseñas para Google Play Store (Para Compartir con Residentes)
                            </h4>
                        </div>
                        <div className="reap-reviews-list">
                            {EJEMPLOS_RESENAS_PLAYSTORE.map((rev, i) => (
                                <div key={i} className="reap-review-item">
                                    <div>
                                        <strong style={{ display: 'block', color: '#0f172a', marginBottom: 2 }}>
                                            {rev.titulo}
                                        </strong>
                                        <span>{rev.texto}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="reap-copy-mini-btn"
                                        onClick={() => handleCopyText(rev.texto, rev.titulo)}
                                    >
                                        <FaCopy /> Copiar
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* TABLA COMPARATIVA MAESTRA CON BOTÓN DE DESCARGA PDF POR FILA */}
            <div className="reap-card">
                <div className="reap-card-head">
                    <div>
                        <h3 className="reap-card-title">
                            Matriz Comparativa en Tiempo Real por Fraccionamiento ({currentPeriodLabel})
                        </h3>
                        <p className="reap-card-sub">
                            Haz clic en cualquier fila para filtrar los graficadores o descarga directamente el PDF para su administrador
                        </p>
                    </div>
                </div>

                <div className="reap-table-wrap">
                    <table className="reap-compact-table">
                        <thead>
                            <tr>
                                <th>Fraccionamiento</th>
                                <th>Viviendas</th>
                                <th>1. Cuentas c/Sesión (Adm/Res/Cas)</th>
                                <th>1. Faltan Iniciar</th>
                                <th>2. Paquetería</th>
                                <th>3. Visitas Manual</th>
                                <th>4. QR Usados</th>
                                <th>5. Aviso Privacidad</th>
                                <th>6. Dispositivos (iOS/Web/App)</th>
                                <th>10. Sesiones (Periodo / Mes Oct)</th>
                                <th>Reporte Admin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {computedReport.fraccionamientos.map((f) => (
                                <tr
                                    key={f.id}
                                    style={{
                                        cursor: 'pointer',
                                        background: selectedFraccId === f.id ? '#eef2ff' : undefined,
                                    }}
                                    onClick={() => setSelectedFraccId(f.id)}
                                >
                                    <td>
                                        <strong>{f.nombre}</strong>
                                        <div style={{ fontSize: '0.67rem', color: '#64748b' }}>
                                            {f.totalUsuariosAsignados} cuentas registradas
                                        </div>
                                    </td>
                                    <td>
                                        <strong>{f.totalCasas}</strong>
                                    </td>
                                    <td>
                                        <strong>{f.everLoggedByRole.administrador}</strong> A ·{' '}
                                        <strong>{f.everLoggedByRole.residente}</strong> R ·{' '}
                                        <strong>{f.everLoggedByRole.caseta}</strong> C
                                        <div className="reap-pbar">
                                            <div
                                                className="reap-pbar-fill"
                                                style={{ width: `${f.porcentajeCoberturaViviendas}%` }}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <strong style={{ color: '#ef4444' }}>
                                            {f.viviendasFaltantesInicio} casas
                                        </strong>{' '}
                                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                                            ({(100 - f.porcentajeCoberturaViviendas).toFixed(1)}%)
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{f.paqueteria}</strong>
                                    </td>
                                    <td>
                                        <strong>{f.visitasManuales}</strong>
                                    </td>
                                    <td>
                                        <strong style={{ color: '#4f46e5' }}>{f.qrValidadosYUsados}</strong>{' '}
                                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                                            / {f.qrGenerados}
                                        </span>
                                    </td>
                                    <td>
                                        <strong style={{ color: '#10b981' }}>{f.privacyAccepted} sí</strong> /{' '}
                                        {f.privacyPending} pend.
                                    </td>
                                    <td>
                                        🍎 {f.loginsByDevice.ios_safari} · 💻 {f.loginsByDevice.navegador_web} · 🤖{' '}
                                        {f.loginsByDevice.android_app}
                                    </td>
                                    <td>
                                        <strong>{f.loginsPeriodoFracc}</strong> en periodo /{' '}
                                        <strong style={{ color: '#0284c7' }}>{f.loginsMesActualFracc}</strong> oct
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            className="reap-copy-mini-btn"
                                            onClick={() => handleGenerateAdminPdf(f)}
                                        >
                                            <FaFilePdf /> PDF Admin
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            <tr className="total-row">
                                <td>TOTAL CONSOLIDADO (6 FRACCS)</td>
                                <td>{t.totalCasas}</td>
                                <td>
                                    {t.everLoggedByRole.administrador} A · {t.everLoggedByRole.residente} R ·{' '}
                                    {t.everLoggedByRole.caseta} C ({t.porcentajeCoberturaViviendas}%)
                                </td>
                                <td>{t.viviendasFaltantesInicio} casas</td>
                                <td>{t.paqueteria}</td>
                                <td>{t.visitasManuales}</td>
                                <td>
                                    {t.qrValidadosYUsados} / {t.qrGenerados}
                                </td>
                                <td>
                                    {t.privacyAccepted} sí / {t.privacyPending} pend.
                                </td>
                                <td>
                                    🍎 {t.loginsByDevice.ios_safari} · 💻 {t.loginsByDevice.navegador_web} · 🤖{' '}
                                    {t.loginsByDevice.android_app}
                                </td>
                                <td>
                                    {t.totalLoginsPeriodo} en periodo / {t.totalLoginsMesActual} oct
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="reap-copy-mini-btn"
                                        onClick={() => handleGenerateAdminPdf(t)}
                                    >
                                        <FaFilePdf /> PDF Global
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReporteEjecutivoAnaliticaPanel;
